/**
 * @fileoverview ClubOS Match Log page — season fixtures, results, and stats
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Mock data
import { matches } from "data/mockData";

// TODO: Replace with API call when backend is ready
// import { coachingService } from "api";

const OUTCOME_COLOR = { W: "success", D: "warning", L: "error" };
const OUTCOME_LABEL = { W: "Win", D: "Draw", L: "Loss" };

const MATCH_DETAIL = {
  1: {
    scorers: ["J. Osei 23'", "S. Holloway 67'", "M. Tetteh 81'"],
    assists: ["F. Rennetti", "K. Pearson", "J. Osei"],
    subs: ["C. Walsh for A. Lefebvre 60'", "A. Popov for J. Osei 75'"],
    ratings: { "J. Hartley": 7.2, "T. Okonkwo": 7.0, "Y. Müller": 7.5, "K. Pearson": 8.1, "F. Rennetti": 7.8 },
  },
  2: {
    scorers: ["S. Holloway 55'"],
    assists: ["J. Osei"],
    subs: ["M. de Bruijn for B. Ashford 70'", "E. Kaya for M. Tetteh 80'"],
    ratings: { "J. Hartley": 6.8, "T. Okonkwo": 6.5, "Y. Müller": 6.9, "K. Pearson": 7.2, "F. Rennetti": 7.0 },
  },
};

function MatchLog() {
  const [expanded, setExpanded] = useState(null);

  // TODO: Uncomment when API is ready
  // const [data, setData] = useState(matches);
  // useEffect(() => { coachingService.getMatches().then(setData).catch(console.error); }, []);

  const played = matches.filter((m) => m.outcome);
  const wins = played.filter((m) => m.outcome === "W").length;
  const draws = played.filter((m) => m.outcome === "D").length;
  const losses = played.filter((m) => m.outcome === "L").length;
  const goalsFor = played.reduce((acc, m) => {
    if (!m.score) return acc;
    const [home, away] = m.score.split("-").map(Number);
    return acc + (m.venue === "H" ? home : away);
  }, 0);
  const goalsAgainst = played.reduce((acc, m) => {
    if (!m.score) return acc;
    const [home, away] = m.score.split("-").map(Number);
    return acc + (m.venue === "H" ? away : home);
  }, 0);
  const points = wins * 3 + draws;
  const recentForm = played.slice(-5).map((m) => m.outcome);

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Match Log
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            2025/26 Season · FC Metropolitan
          </SoftTypography>
        </SoftBox>

        {/* Season stats */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Points" }}
                count={String(points)}
                percentage={{ color: "info", text: `${played.length} played` }}
                icon={{ color: "info", component: "emoji_events" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Record (W/D/L)" }}
                count={`${wins}/${draws}/${losses}`}
                percentage={{ color: "success", text: "this season" }}
                icon={{ color: "success", component: "sports_soccer" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Goals For" }}
                count={String(goalsFor)}
                percentage={{ color: "success", text: `${goalsAgainst} against` }}
                icon={{ color: "success", component: "sports" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Goal Difference" }}
                count={goalsFor - goalsAgainst >= 0 ? `+${goalsFor - goalsAgainst}` : String(goalsFor - goalsAgainst)}
                percentage={{ color: goalsFor >= goalsAgainst ? "success" : "error", text: "GD" }}
                icon={{ color: "dark", component: "timeline" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Recent form */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={2.5} display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <SoftTypography variant="h6" fontWeight="medium">
                Recent Form
              </SoftTypography>
              <SoftBox display="flex" gap={1}>
                {recentForm.map((r, i) => (
                  <SoftBox
                    key={i}
                    width={36}
                    height={36}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      backgroundColor:
                        r === "W" ? "#4caf50" : r === "D" ? "#ff9800" : "#f44336",
                    }}
                  >
                    <SoftTypography variant="caption" color="white" fontWeight="bold">
                      {r}
                    </SoftTypography>
                  </SoftBox>
                ))}
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                Last {recentForm.length} matches
              </SoftTypography>
            </SoftBox>
          </Card>
        </SoftBox>

        {/* Fixtures / results table */}
        <Card>
          <SoftBox p={3}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="h6" fontWeight="medium">
                Season Fixtures &amp; Results
              </SoftTypography>
              <SoftButton variant="gradient" color="info" size="small">
                Export Report
              </SoftButton>
            </SoftBox>

            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Date", "Competition", "Home", "Score", "Away", "Venue", "Formation", "Outcome", ""].map((h) => (
                      <SoftBox
                        key={h}
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 1.5,
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          borderBottom: "2px solid #e9ecef",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {matches.map((match) => (
                    <>
                      <SoftBox
                        key={`row-${match.id}`}
                        component="tr"
                        sx={{
                          cursor: match.outcome ? "pointer" : "default",
                          "&:hover": { backgroundColor: match.outcome ? "#f8f9fa" : "transparent" },
                          backgroundColor: expanded === match.id ? "#eef6ff" : "white",
                        }}
                        onClick={() => match.outcome && toggleExpand(match.id)}
                      >
                        {/* Date */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                          <SoftTypography variant="caption" color="text">
                            {new Date(match.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })}
                          </SoftTypography>
                        </SoftBox>

                        {/* Competition */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge
                            badgeContent={match.competition === "Premier Division" ? "PD" : match.competition === "League Cup" ? "LC" : "EUR"}
                            color={match.competition === "Premier Division" ? "info" : match.competition === "League Cup" ? "warning" : "success"}
                            size="xs"
                            container
                          />
                        </SoftBox>

                        {/* Home */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="button" fontWeight={match.venue === "H" ? "bold" : "regular"}>
                            {match.home}
                          </SoftTypography>
                        </SoftBox>

                        {/* Score */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", textAlign: "center" }}>
                          {match.score ? (
                            <SoftTypography variant="button" fontWeight="bold">
                              {match.score}
                            </SoftTypography>
                          ) : (
                            <SoftTypography variant="caption" color="text">
                              {match.time || "TBC"}
                            </SoftTypography>
                          )}
                        </SoftBox>

                        {/* Away */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="button" fontWeight={match.venue === "A" ? "bold" : "regular"}>
                            {match.away}
                          </SoftTypography>
                        </SoftBox>

                        {/* Venue */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge
                            badgeContent={match.venue === "H" ? "Home" : "Away"}
                            color={match.venue === "H" ? "primary" : "secondary"}
                            size="xs"
                            container
                          />
                        </SoftBox>

                        {/* Formation */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">
                            {match.formation || "—"}
                          </SoftTypography>
                        </SoftBox>

                        {/* Outcome */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          {match.outcome ? (
                            <SoftBadge
                              badgeContent={OUTCOME_LABEL[match.outcome]}
                              color={OUTCOME_COLOR[match.outcome]}
                              size="xs"
                              container
                            />
                          ) : (
                            <SoftBadge badgeContent="Upcoming" color="info" size="xs" container />
                          )}
                        </SoftBox>

                        {/* Expand toggle */}
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          {match.outcome && (
                            <SoftTypography variant="caption" color="info">
                              {expanded === match.id ? "▲" : "▼"}
                            </SoftTypography>
                          )}
                        </SoftBox>
                      </SoftBox>

                      {/* Detail row */}
                      {expanded === match.id && (
                        <SoftBox key={`detail-${match.id}`} component="tr">
                          <SoftBox
                            component="td"
                            colSpan={9}
                            sx={{ backgroundColor: "#f7fbff", px: 3, py: 2, borderBottom: "1px solid #e9ecef" }}
                          >
                            <Grid container spacing={3}>
                              {/* Scorers */}
                              <Grid item xs={12} sm={4}>
                                <SoftTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                                  GOALSCORERS
                                </SoftTypography>
                                {(MATCH_DETAIL[match.id]?.scorers || ["No scorer data"]).map((s, i) => (
                                  <SoftTypography key={i} variant="caption" display="block" color="dark">
                                    ⚽ {s}
                                  </SoftTypography>
                                ))}
                              </Grid>

                              {/* Substitutions */}
                              <Grid item xs={12} sm={4}>
                                <SoftTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                                  SUBSTITUTIONS
                                </SoftTypography>
                                {(MATCH_DETAIL[match.id]?.subs || ["No substitution data"]).map((s, i) => (
                                  <SoftTypography key={i} variant="caption" display="block" color="dark">
                                    🔄 {s}
                                  </SoftTypography>
                                ))}
                              </Grid>

                              {/* Top ratings */}
                              <Grid item xs={12} sm={4}>
                                <SoftTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                                  TOP PERFORMERS
                                </SoftTypography>
                                {Object.entries(MATCH_DETAIL[match.id]?.ratings || {})
                                  .sort(([, a], [, b]) => b - a)
                                  .slice(0, 3)
                                  .map(([name, rating]) => (
                                    <SoftBox key={name} display="flex" justifyContent="space-between" mb={0.5}>
                                      <SoftTypography variant="caption" color="dark">{name}</SoftTypography>
                                      <SoftBadge
                                        badgeContent={String(rating)}
                                        color={rating >= 7.5 ? "success" : rating >= 7 ? "info" : "secondary"}
                                        size="xs"
                                        container
                                      />
                                    </SoftBox>
                                  ))}
                              </Grid>
                            </Grid>
                          </SoftBox>
                        </SoftBox>
                      )}
                    </>
                  ))}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default MatchLog;
